import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { customerAPI } from '../api';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail } from 'lucide-react';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const filtered = customers.filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="" data-testid="customers-page">
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Customers</h1>
            <p className="text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Manage your customer base</p>
          </div>
          <Link to="/customers/new">
            <Button data-testid="create-customer-button" size="sm" className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          </Link>
        </div>


        <div className="stats-card">
          {loading ? (
            <div className="text-center py-12 text-[var(--tms-text-secondary)]">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Contact</th>
                    <th>Account Status</th>
                    <th>Credit Limit</th>
                    <th>Credit Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-12 text-[var(--tms-text-muted)]">
                        {searchTerm ? 'No customers found' : 'No customers yet. Add your first customer to get started.'}
                      </td>
                    </tr>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                        <td>
                          <Link to={`/customers/${customer.id}`} className="font-semibold text-[var(--tms-primary)] hover:text-blue-700" data-testid={`customer-link-${customer.id}`}>
                            {customer.name}
                          </Link>
                        </td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <a href={`tel:${customer.phone}`} className="flex items-center gap-1 text-sm text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)]">
                              <Phone className="w-3 h-3" /> {customer.phone}
                            </a>
                            <a href={`mailto:${customer.email}`} className="flex items-center gap-1 text-sm text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)]">
                              <Mail className="w-3 h-3" /> {customer.email}
                            </a>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${
                            customer.account_status === 'Active' ? 'status-delivered' :
                            customer.account_status === 'On Hold' ? 'status-delayed' :
                            'status-available'
                          }`}>
                            {customer.account_status}
                          </span>
                        </td>
                        <td className="font-mono">${customer.credit_limit.toLocaleString()}</td>
                        <td className="font-mono">${customer.credit_usage.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Customers;